import { useMemo } from "react";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import type { VesselNominationOutletContext } from "@features/vessel-nomination/add/constants";
import { FORM_FIELDS } from "@features/vessel-nomination/add/constants";
import type { VesselNominationFormData } from "@features/vessel-nomination/add/schema/vesselNominationFormSchema";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import type { Port } from "@services/vessel/types";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { LANG_BASE } from "../PreArrivalQuestionnaire";

const VoyageInformation = () => {
  const { t } = useTranslation();
  const { formData, updateFormData } = useOutletContext<VesselNominationOutletContext>();

  const securityLevelOptions = useMemo(
    () =>
      ["1", "2", "3"].map((level) => ({
        label: level,
        value: `LEVEL_${level}`,
      })),
    [],
  );

  const { options: ports } = useFormattedReference<Port>(REFERENCE_DATA_TYPES.PORTS, "bff-vessel");
  const portOptions = useMemo(() => {
    if (!ports) return [];
    return ports
      .sort((a, b) => a.portName!.localeCompare(b.portName!))
      .map((port) => ({
        label: port.portName!,
        value: port.portCode!,
      }));
  }, [ports]);

  return (
    <FormProvider {...formData}>
      <FormSection
        title={t(`${LANG_BASE}.sections.voyage-information`)}
        className="grid-cols-1 lg:grid-cols-2"
      >
        <FormItem<VesselNominationFormData, typeof FORM_FIELDS.LAST_PORT>
          testId="nomination-last-port-input"
          name={FORM_FIELDS.LAST_PORT}
          type="autocomplete"
          label={t(`${LANG_BASE}.fields.last-port-of-call`)}
          onFieldChange={updateFormData}
          options={portOptions}
        />
        <FormItem<VesselNominationFormData, typeof FORM_FIELDS.NEXT_PORT>
          testId="nomination-next-port-input"
          name={FORM_FIELDS.NEXT_PORT}
          type="autocomplete"
          label={t(`${LANG_BASE}.fields.next-port-of-call`)}
          onFieldChange={updateFormData}
          options={portOptions}
        />
        <FormItem<VesselNominationFormData, typeof FORM_FIELDS.SECURITY_LEVEL>
          testId="nomination-security-level-input"
          name={FORM_FIELDS.SECURITY_LEVEL}
          type="select"
          label={t(`${LANG_BASE}.fields.security-level`)}
          onFieldChange={updateFormData}
          options={securityLevelOptions}
        />
      </FormSection>
    </FormProvider>
  );
};

export default VoyageInformation;
